import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCommission, updateCommission } from '../../api';
import { PageHeader, TableSkeleton } from '../../components/ui';
import toast from 'react-hot-toast';
import { Percent } from 'lucide-react';

type FormValues = {
  chat: number;
  call: number;
  video: number;
  appointment: number;
  emergency: number;
};

const MODES: { key: keyof FormValues; label: string; hint: string }[] = [
  { key: 'chat', label: 'Chat', hint: 'Instant chat consultations' },
  { key: 'call', label: 'Audio', hint: 'Audio call consultations (code: call)' },
  { key: 'video', label: 'Video', hint: 'Video consultations' },
  { key: 'appointment', label: 'Appointment', hint: 'Scheduled appointments' },
  { key: 'emergency', label: 'Emergency', hint: 'Priority / emergency sessions' },
];

export default function CommissionPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-commission'],
    queryFn: () => getCommission().then((r) => r.data.data),
  });

  const { register, handleSubmit, reset, formState: { isDirty } } = useForm<FormValues>({
    defaultValues: { chat: 0.2, call: 0.2, video: 0.2, appointment: 0.2, emergency: 0.2 },
  });

  useEffect(() => {
    if (data?.rates) reset(data.rates);
  }, [data, reset]);

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const rates: FormValues = {
        chat: Number(values.chat),
        call: Number(values.call),
        video: Number(values.video),
        appointment: Number(values.appointment),
        emergency: Number(values.emergency),
      };
      for (const v of Object.values(rates)) {
        if (!Number.isFinite(v) || v < 0 || v > 1) throw new Error('Rates must be between 0 and 1');
      }
      return updateCommission({ rates });
    },
    onSuccess: () => {
      toast.success('Commission rates saved');
      qc.invalidateQueries({ queryKey: ['admin-commission'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e?.message || 'Save failed'),
  });

  return (
    <div className="p-6 max-w-3xl">
      <PageHeader
        title="Commission"
        subtitle="Per-mode platform commission rates (0–1 fraction). Never hardcoded in billing."
      />

      {isLoading ? (
        <TableSkeleton rows={5} cols={2} />
      ) : (
        <form onSubmit={handleSubmit((v) => save.mutate(v))} className="card p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <Percent size={14} /> Enter fraction (e.g. 0.25 = 25%). UI shows percent for reference.
          </div>
          {MODES.map((m) => (
            <div key={m.key} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end border-b border-gray-50 pb-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">{m.label}</p>
                <p className="text-xs text-gray-500">{m.hint}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Rate (0–1)</label>
                <input type="number" step="0.01" min={0} max={1} className="input" {...register(m.key)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Percent</label>
                <p className="text-sm font-bold text-primary-600">
                  {data?.ratesPercent?.[m.key] != null
                    ? `${data.ratesPercent[m.key]}%`
                    : '—'}
                </p>
              </div>
            </div>
          ))}
          <div className="flex justify-end pt-2">
            <button type="submit" className="btn-primary" disabled={!isDirty || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save rates'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
